/* ===================================
   Fire Zipline - JavaScript (clean)
   Mobile-first, robust, Shopify-safe
   ================================== */

(function () {
  'use strict';

  // -----------------------------
  // Utils
  // -----------------------------
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const $ = (sel, ctx = document) => ctx.querySelector(sel);

  function clampInt(n, min, max) {
    n = parseInt(n, 10);
    if (Number.isNaN(n)) n = min;
    return Math.min(max ?? n, Math.max(min, n));
  }

  function parsePriceFromText(text) {
    if (!text) return null;
    const cleaned = String(text).replace(/\s/g, '').replace(',', '.');
    const match = cleaned.match(/([0-9]+(?:\.[0-9]{1,2})?)/);
    if (!match) return null;
    const val = parseFloat(match[1]);
    return Number.isFinite(val) ? val : null;
  }

  function formatEUR(value) {
    const v = Math.max(0, Number(value) || 0);
    return v.toFixed(2).replace('.', ',') + '€';
  }

  // -----------------------------
  // 1) Scroll Reveal
  // -----------------------------
  function initScrollReveal() {
    const els = $$('.reveal-element');
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('reveal-visible');
          obs.unobserve(entry.target); // perf
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -80px 0px' }
    );

    els.forEach((el) => obs.observe(el));
  }

  // -----------------------------
  // 2) Header Scroll Behavior
  // -----------------------------
  function initScrollHeader() {
    const header = document.querySelector('[data-scroll-header]');
    if (!header) return;

    const onScroll = () => {
      header.setAttribute('data-scrolled', window.scrollY > 10 ? 'true' : 'false');
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // -----------------------------
  // 3) Quantity + Total
  // IMPORTANT:
  // - We ONLY bind on quantity selector blocks to avoid double-binding
  //   (bug: qty jumps 1->3->5... when bound twice).
  // -----------------------------
  function initQuantitySelectors() {
    const selectors = $$('.fz-quantity-selector, .fz-quantity-selector-inline');
    if (!selectors.length) return;

    const DISCOUNT_PER_EXTRA = 0.20; // -0,20€ per extra unit

    selectors.forEach((selector) => {
      // Guard against any accidental re-init
      if (selector.dataset.qtyInit === 'true') return;
      selector.dataset.qtyInit = 'true';

      const dec = selector.querySelector('[data-decrease]');
      const inc = selector.querySelector('[data-increase]');
      const input = selector.querySelector('[data-quantity]');
      const totalEl =
        selector.closest('form')?.querySelector('[data-total]') ||
        selector.closest('.fz-cta-form')?.querySelector('[data-total]') ||
        selector.querySelector('[data-total]');

      if (!input) return;

      // Try to find unit price
      let unit = null;

      // 1) explicit attribute on selector OR parent form
      const attrPrice =
        selector.getAttribute('data-unit-price') ||
        selector.closest('form')?.getAttribute('data-unit-price') ||
        selector.closest('section')?.getAttribute('data-unit-price');

      if (attrPrice) {
        const v = parseFloat(String(attrPrice).replace(',', '.'));
        unit = Number.isFinite(v) ? v : null;
      }

      // 2) from total display initial money text
      if (unit === null && totalEl) {
        unit = parsePriceFromText(totalEl.textContent);
      }

      // 3) fallback: nearby price badge in same section
      if (unit === null) {
        const nearby = selector.closest('section')?.querySelector('.fz-price-badge, .fz-price-large, [data-price]');
        if (nearby) unit = parsePriceFromText(nearby.textContent);
      }

      // 4) last fallback
      if (unit === null) unit = 4.99;

      function computeTotal(qty) {
        const discount = Math.max(0, (qty - 1) * DISCOUNT_PER_EXTRA);
        return (qty * unit) - discount;
      }

      function update() {
        const qty = clampInt(input.value, 1);
        input.value = qty;

        if (totalEl) {
          totalEl.textContent = formatEUR(computeTotal(qty));
        }
      }

      if (dec) {
        dec.addEventListener('click', () => {
          const current = clampInt(input.value, 1);
          input.value = Math.max(1, current - 1);
          update();
        });
      }

      if (inc) {
        inc.addEventListener('click', () => {
          const current = clampInt(input.value, 1);
          input.value = current + 1;
          update();
        });
      }

      input.addEventListener('input', update);
      input.addEventListener('change', update);

      update();
    });
  }

  // -----------------------------
  // 4) Smooth scroll for anchors
  // -----------------------------
  function initSmoothScroll() {
    const anchors = $$('a[href^="#"]');
    if (!anchors.length) return;

    anchors.forEach((a) => {
      a.addEventListener(
        'click',
        function (e) {
          const href = this.getAttribute('href');
          if (!href || href === '#') return;

          const target = document.querySelector(href);
          if (!target) return;

          if (e.defaultPrevented) return;

          e.preventDefault();

          const header = document.querySelector('[data-scroll-header]');
          const headerH = header ? header.offsetHeight : 80;

          const top = target.getBoundingClientRect().top + window.scrollY - headerH - 10;
          window.scrollTo({ top, behavior: 'smooth' });
          history.pushState(null, '', href);
        },
        { passive: false }
      );
    });
  }

  // -----------------------------
  // 5) Add to cart (ajax)
  // -----------------------------
  function initAddToCart() {
    const forms = $$('form[action="/cart/add"]');
    if (!forms.length) return;

    forms.forEach((form) => {
      // Guard against double-bind (Shopify sections can re-render)
      if (form.dataset.atcInit === 'true') return;
      form.dataset.atcInit = 'true';

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const button = form.querySelector('button[type="submit"], input[type="submit"]');
        const original = button ? (button.tagName === 'INPUT' ? button.value : button.textContent) : null;

        try {
          if (button) {
            button.disabled = true;
            if (button.tagName === 'INPUT') button.value = 'Ajout…';
            else button.textContent = 'Ajout…';
          }

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'application/json' },
          });

          const data = await res.json();

          if (!res.ok) {
            const msg = data?.description || data?.message || 'Une erreur est survenue.';
            throw new Error(msg);
          }

          if (button) {
            if (button.tagName === 'INPUT') button.value = 'Ajouté ✓';
            else button.textContent = 'Ajouté ✓';
          }

          setTimeout(() => {
            window.location.href = '/cart';
          }, 650);
        } catch (err) {
          console.error('Add to cart error:', err);
          alert(err?.message || 'Une erreur est survenue. Réessaie.');

          if (button) {
            button.disabled = false;
            if (button.tagName === 'INPUT') button.value = original || 'Ajouter au panier';
            else button.textContent = original || 'Ajouter au panier';
          }
        }
      });
    });
  }

  // -----------------------------
  // 6) FAQ accordion
  // -----------------------------
  function initFAQ() {
    const questions = $$('[data-faq-question]');
    if (!questions.length) return;

    function closeAll(exceptBtn) {
      questions.forEach((btn) => {
        if (btn === exceptBtn) return;
        btn.classList.remove('active');
        const ans = btn.nextElementSibling;
        if (ans) ans.style.maxHeight = null;
      });
    }

    questions.forEach((btn) => {
      // Guard
      if (btn.dataset.faqInit === 'true') return;
      btn.dataset.faqInit = 'true';

      btn.addEventListener('click', () => {
        const ans = btn.nextElementSibling;
        if (!ans) return;

        const isOpen = btn.classList.contains('active');
        closeAll(btn);

        if (isOpen) {
          btn.classList.remove('active');
          ans.style.maxHeight = null;
        } else {
          btn.classList.add('active');
          ans.style.maxHeight = ans.scrollHeight + 'px';
        }
      });
    });

    window.addEventListener('resize', () => {
      questions.forEach((btn) => {
        if (!btn.classList.contains('active')) return;
        const ans = btn.nextElementSibling;
        if (!ans) return;
        ans.style.maxHeight = ans.scrollHeight + 'px';
      });
    });
  }

  // -----------------------------
  // 7) Video lazy load
  // -----------------------------
  function initVideoLazyLoad() {
    const videoEls = $$('video[data-src]');
    const sourceVideos = $$('video source[data-src]')
      .map((s) => s.closest('video'))
      .filter(Boolean);

    const targets = Array.from(new Set([...videoEls, ...sourceVideos]));
    if (!targets.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const video = entry.target;

          if (video.dataset.src) {
            video.src = video.dataset.src;
            video.removeAttribute('data-src');
          }

          $$('source[data-src]', video).forEach((src) => {
            src.src = src.dataset.src;
            src.removeAttribute('data-src');
          });

          try {
            video.load();
          } catch (e) {}

          obs.unobserve(video);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px 120px 0px' }
    );

    targets.forEach((v) => obs.observe(v));
  }

  // -----------------------------
  // Init
  // -----------------------------
  function init() {
    initScrollReveal();
    initScrollHeader();
    initQuantitySelectors();
    initSmoothScroll();
    initAddToCart();
    initFAQ();
    initVideoLazyLoad();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();